
import Vue from "vue";
import { ISelectItem } from "winfakt-vue-components";
import { AccountantService, IAccountantDossier, INotificationSettings, NotificationFrequency } from "@/services/accountant-service";
import { printLocalDateTime } from "@/utils/pretty-print";

const FREQUENCIES:NotificationFrequency[] = ["daily", "weekly", "monthly"];

export default Vue.extend({
	data() {
		return {
			settings: {
				Enabled: false,
				Frequency: "weekly",
				NotifyOnError: true,
				Email: "",
				DossierIDs: [],
				NextSendAt: null,
				LastSentAt: null
			} as INotificationSettings,
			dossiers: [] as IAccountantDossier[],
			dossierMode: "all" as "all" | "selection",
			loaded: false,
			busy: false,
			savedOnce: false
		};
	},
	computed: {
		frequencyItems():ISelectItem<string>[] {
			return FREQUENCIES.map(f => ({text: this.$ct(`frequencies.${f}`).toString(), value: f}));
		},
		canTest():boolean {
			return this.savedOnce && this.settings.Enabled;
		}
	},
	methods: {
		formatDate(d:string):string {
			return printLocalDateTime(new Date(d));
		},
		integrationLabel(d:IAccountantDossier):string {
			if (!d.IntegrationType || d.IntegrationType == "manual") return this.$ct("no-integration").toString();
			return d.IntegrationType;
		},
		isSelected(d:IAccountantDossier):boolean {
			return this.settings.DossierIDs.indexOf(d.DossierID) >= 0;
		},
		toggleDossier(d:IAccountantDossier, checked:boolean) {
			let ids = this.settings.DossierIDs.filter(id => id != d.DossierID);
			if (checked) ids.push(d.DossierID);
			this.settings.DossierIDs = ids;
		},
		async load() {
			let [settings, dossiers] = await Promise.all([
				AccountantService.getNotificationSettings(),
				AccountantService.getDossiers()
			]);
			this.settings = settings;
			this.dossiers = dossiers;
			this.dossierMode = settings.DossierIDs.length ? "selection" : "all";
			this.savedOnce = !!settings.NextSendAt || !!settings.LastSentAt || settings.Enabled;
			this.loaded = true;
		},
		async save() {
			this.busy = true;
			try {
				let payload = {...this.settings, DossierIDs: this.dossierMode == "all" ? [] : this.settings.DossierIDs};
				this.settings = await AccountantService.saveNotificationSettings(payload);
				this.dossierMode = this.settings.DossierIDs.length ? "selection" : "all";
				this.savedOnce = true;
				this.$wf.notify(this.$ct("saved").toString(), "success", 3000, "check");
			} catch (err) {
				this.$wf.notify(this.$ct("save-error").toString(), "error", 4000, "exclamation-triangle");
			} finally {
				this.busy = false;
			}
		},
		async sendErrorTest() {
			this.busy = true;
			try {
				let result = await AccountantService.sendNotificationErrorTest();
				let key = result.IsSample ? "error-test-sent-sample" : "error-test-sent";
				this.$wf.notify(this.$ct(key, {email: result.Email, dossiers: result.Dossiers}).toString(), "success", 6000, "check");
			} catch (err) {
				this.$wf.notify(this.$ct("test-error").toString(), "error", 4000, "exclamation-triangle");
			} finally {
				this.busy = false;
			}
		},
		async sendTest() {
			this.busy = true;
			try {
				let result = await AccountantService.sendNotificationTest();
				this.$wf.notify(this.$ct("test-sent", {email: result.Email, dossiers: result.Dossiers, period: result.Period}).toString(), "success", 6000, "check");
			} catch (err) {
				this.$wf.notify(this.$ct("test-error").toString(), "error", 4000, "exclamation-triangle");
			} finally {
				this.busy = false;
			}
		}
	},
	async mounted() {
		await this.load();
	},
	i18nEx: {
		componentPrefix: "views.AccountantNotifications"
	}
});
